import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListParams,
  GetListResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import _isEqual from "lodash/isEqual";
import { z } from "zod";
import { v4 as randomUuid } from "uuid";

type AdasDto = z.infer<typeof schemas.AdasDto>;

export type Adas = AdasDto;

class AdasProvider extends AbstractDataProvider<Adas> {
  async getOne(_resource: string, { id }: GetOneParams<Adas>): Promise<GetOneResult<Adas>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getAdas({ params: { id } });
    return { data: _dtoToRecord(dto) };
  }

  async getMany(_resource: string, { ids }: { ids: Array<string> }): Promise<GetManyResult<Adas>> {
    const serverClient = await getAuthenticatedServerClient();
    const dtos = await Promise.all(ids.map((id) => serverClient.getAdas({ params: { id } })));
    return { data: dtos.map(_dtoToRecord) };
  }

  async getList(_resource: string, { pagination, sort, filter }: GetListParams): Promise<GetListResult<Adas>> {
    const serverClient = await getAuthenticatedServerClient();
    const dtos = await serverClient.listAdas();
    const records = dtos.map(_dtoToRecord);
    return applyPagination(applySort(applyFilter(records, filter), sort), pagination);
  }

  async getManyReference(
    resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<Adas>> {
    return this.getList(resource, { filter: { ...filter, [target]: id }, sort, pagination });
  }

  async create(_resource: string, { data }: CreateParams<Adas>): Promise<CreateResult<Adas>> {
    const createDto = schemas.CreateAdasDto.parse({ ...data, idempotencyKey: randomUuid() });
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.createAdas(createDto);
    return { data: _dtoToRecord(dto) };
  }

  async update(_resource: string, { id, previousData, data }: UpdateParams<Adas>): Promise<UpdateResult<Adas>> {
    const delta = Object.fromEntries(Object.entries(data).filter(([k, v]) => !_isEqual(v, previousData[k])));
    const updateDto = schemas.UpdateAdasDto.parse(delta);
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.updateAdas(updateDto, { params: { id } });
    return { data: _dtoToRecord(dto) };
  }
}

function _dtoToRecord(dto: AdasDto): Adas {
  return dto;
}

export const adasProvider = new AdasProvider() as DataProvider;
